import {ValidColorT} from '@/lib/types/colors.types';
import {AllIconNamesT} from '@/lib/types/icons/all-icons';

import {COLOR_CONFIGS} from '@/lib/color-configs';
import {cn} from '@/lib/utils/cn';

import Icon, {IconPropsT} from '@/components/icon';

export type ButtonPropsT = {
  // Core Props
  children?: React.ReactNode;
  className?: string;
  onClick?:
    | ((event: React.MouseEvent<HTMLButtonElement>) => void)
    | ((event: React.MouseEvent<HTMLButtonElement>) => Promise<void>);
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;

  // Style Props
  color?: ValidColorT;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'basic' | 'solid' | 'outline' | 'ghost' | 'gradient';
  shape?: 'rounded' | 'rounded-full' | 'rounded-lg' | 'rounded-md' | 'rounded-sm';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'color';
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'auto' | 'full';
  wave?: boolean;
  active?: boolean;
  focus?: boolean;
  hover?: boolean;
  pressed?: boolean;

  // Icon Props
  icon?: AllIconNamesT;
  hideIcon?: boolean;
  iconOnly?: boolean;
  iconColor?: ValidColorT;
  iconHoverColor?: ValidColorT;
  iconActiveColor?: ValidColorT;
  iconSize?: number | 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  iconPosition?: 'left' | 'right';
  iconFlip?: 'horizontal' | 'vertical';
  iconRotate?: '0' | '45' | '90' | '180' | '270';
  iconAnimate?: 'spin' | 'pulse' | 'bounce' | 'ping' | 'bell' | 'wase' | 'spin-slow' | 'slow-ping';

  // Loading Props
  isLoading?: boolean;
  loadingText?: string;
  loadingIcon?: AllIconNamesT;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

export const Button = ({
  children,
  className,
  onClick,
  type = 'button',
  color = 'primary',
  size = 'md',
  variant = 'basic',
  shape = 'rounded',
  icon,
  hideIcon = false,
  iconOnly = false,
  iconColor = COLOR_CONFIGS[color]?.[variant]?.icon,
  iconHoverColor = COLOR_CONFIGS[color]?.[variant]?.iconHover,
  iconActiveColor = COLOR_CONFIGS[color]?.[variant]?.iconActive,
  iconSize = 'base',
  iconPosition = 'left',
  iconFlip,
  iconRotate,
  iconAnimate,
  isLoading = false,
  loadingText,
  loadingIcon = 'loader',
  shadow = 'none',
  wave = false,
  width = 'auto',
  active = false,
  focus = false,
  hover = false,
  pressed = false,
  ...htmlButtonProps
}: ButtonPropsT) => {
  const buttonClasses = cn(
    'group ti-btn',
    // Base variant classes
    {
      // Basic variant (default)
      [`ti-btn-${color}`]: variant === 'basic',
      // Solid variant (full color)
      [`ti-btn-${color}-full`]: variant === 'solid',
      // Outline variant
      [`ti-btn-outline ti-btn-outline-${color}`]: variant === 'outline',
      // Ghost variant
      [`ti-btn-ghost-${color}`]: variant === 'ghost',
      // Gradient variant
      [`ti-btn-${color}-gradient`]: variant === 'gradient',
    },
    // Size
    `ti-btn-${size}`,
    // Shape - apply with prefix
    `ti-btn-${shape}`,
    // Width
    {
      [`ti-btn-w-${width}`]: width !== 'auto',
      'w-full': width === 'full',
    },
    // Shadow - apply with prefix
    {
      'ti-btn-shadow-sm': shadow === 'sm',
      'ti-btn-shadow': shadow === 'md',
      'ti-btn-shadow-lg': shadow === 'lg',
      [`shadow-${color}`]: shadow === 'color',
    },
    // States
    {
      'ti-btn-disabled': isLoading || htmlButtonProps.disabled,
      'ti-btn-loader': isLoading,
      'ti-btn-wave': wave,
      'p-2': iconOnly,
      'ti-btn-active': active,
      focus: focus,
      hover: hover,
      pressed: pressed,
    },
    // Add flex alignment for icon positioning
    'inline-flex items-center justify-center',
    className
  );

  // Get the default brand icon based on color
  const defaultBrandIcon =
    !hideIcon && color in SOCIAL_BRAND_ICONS ? SOCIAL_BRAND_ICONS[color as keyof typeof SOCIAL_BRAND_ICONS] : undefined;

  // Determine icon size based on button size if not explicitly set
  const calculatedIconSize = iconSize || (size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'base');

  const iconProps: IconPropsT = {
    name: (icon || defaultBrandIcon || 'user') as AllIconNamesT,
    color: iconColor,
    hoverColor: iconHoverColor,
    activeColor: iconActiveColor,
    size: calculatedIconSize,
    isInButton: true,
    variant,
    flip: iconFlip,
    rotate: iconRotate,
    animate: iconAnimate,
    isActive: active,
    // Adjust spacing between icon and text
    className: cn('transition-colors', {
      'me-1': iconPosition === 'left' && children && !iconOnly,
      'ms-1': iconPosition === 'right' && children && !iconOnly,
    }),
  };

  const iconElement = !hideIcon && (icon || defaultBrandIcon) && <Icon {...iconProps} />;

  return (
    <button {...htmlButtonProps} type={type} onClick={onClick} className={buttonClasses}>
      {isLoading ? (
        <>
          <Icon name={loadingIcon} className="me-1.5 animate-spin" isInButton size={calculatedIconSize} />
          {loadingText || children}
        </>
      ) : (
        <>
          {iconPosition === 'left' && iconElement}
          {!iconOnly && children}
          {iconPosition === 'right' && iconElement}
        </>
      )}
    </button>
  );
};

const SOCIAL_BRAND_ICONS = {
  facebook: 'brand-facebook',
  google: 'brand-google',
  twitter: 'brand-twitter',
  github: 'brand-github',
  youtube: 'brand-youtube',
  instagram: 'brand-instagram',
} as const;

export default Button;
